import { Injectable } from '@angular/core';
import { App, AppInfo } from '@capacitor/app';
import { PlatformService } from './platform.service';
import { logger } from 'src/app/common/services/logging/logging.service';
import { AsyncSubject, combineLatest, filter, Observable, take } from 'rxjs';
import { AppConstants } from 'src/app/system/core/AppConstants';
import { DeviceInfoService } from './device-info.service';
import { NetworkService } from './network.service';
import { NavigationEnd, Router } from '@angular/router';
import { DeepLinkOpenKind, DeepLinkUtility } from './deep-link.utility';
import { QrDeepLinkService } from 'src/app/common/services/qr-deep-link/qr-deep-link.service';
import { NavigationService } from './navigation.service';

@Injectable({
  providedIn: 'root'
})
export class AppService {

  private static appInfo: AppInfo = AppService.getBrowserWebInfo();
  public initialized$: AsyncSubject<boolean> = new AsyncSubject<boolean>();

  constructor(
    private readonly srvPlatform: PlatformService,
    private readonly srvDeviceInfo: DeviceInfoService,
    private readonly srvNetwork: NetworkService,
    private readonly router: Router,
    private readonly srvQrDeepLink: QrDeepLinkService,
  ) {

    if (PlatformService.isMobile()) {
      App.addListener('appStateChange', ({ isActive }) => {
        logger.debug('App state changed. Is active?', isActive);
      });

      App.addListener('appUrlOpen', (data: any) => {
        logger.debug('App opened with URL:', data);
        this.handleOpenUrl(data);
      });

      App.addListener('appRestoredResult', data => {
        logger.debug('Restored state:', data);
      });

      App.addListener('resume', async () => {
        logger.debug('Resume state:');
      });
    }

  }

  private handleOpenUrl(data: any) {
    if (!data?.url) return;
    logger.debug('🔗 Deep Link received:', data.url);

    let action = DeepLinkUtility.resolveOpen(data.url);
    switch (action.kind) {
      case DeepLinkOpenKind.QR_CODE:
        this.srvQrDeepLink.open(action.key);
        return;
      case DeepLinkOpenKind.ID_SCAN:
        void NavigationService.goto(this.router, '/id-scan', { queryParams: { token: action.token } });
        return;
      case DeepLinkOpenKind.INVALID:
        logger.warn('Deep link ignored:', data.url);
        return;
    }
  }

  public init() {

    // Please note these services does not required External Settings
    combineLatest([
      this.initAppInfo(),
      this.srvPlatform.init(),
      this.srvDeviceInfo.init(),
      this.srvNetwork.init(),
    ]).subscribe(([info]) => {
      AppService.appInfo = info;
      this.checkLaunchUrl();
      this.markInit(true);
    });
  }

  private async checkLaunchUrl() {
    if (!PlatformService.isMobile()) return;

    try {
      const data = await App.getLaunchUrl();
      if (data?.url) {
        logger.debug('Cold Start Launch URL found:', data.url);

        // Preserve the URL immediately
        // AppService.appUrlOpen = data;
        // This ensures Angular is stable and ready to accept a new route.
        if (this.router.navigated) {
          logger.debug('App is stable (already navigated), performing deep link redirect now.');
          this.handleOpenUrl(data);
        } else {
          this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            take(1) // Only do this once at boot
          ).subscribe(() => {
            logger.debug('App is stable, performing deep link redirect now.');
            this.handleOpenUrl(data);
          });
        }
      }
    } catch (error) {
      logger.error('Error checking launch URL:', error);
    }
  }

  /**
   * ExternalSettings will be invoked by Resolver which will depend on this.srvApp.initialized$
   * Please note some of the static services are initialized inside ExternalSettingsService
  */
  public markInit(value: boolean) {
    this.initialized$.next(value);
    this.initialized$.complete();
  }

  public get isInitialized(): Observable<boolean> {
    return this.initialized$.asObservable();
  }

  private initAppInfo(): AsyncSubject<AppInfo> {
    let ret = new AsyncSubject<AppInfo>();

    if (PlatformService.isBrowser()) {
      ret.next(AppService.getBrowserWebInfo());
      ret.complete();
    }
    else {
      App.getInfo().then((info) => {
        ret.next(info);
        ret.complete();
      });
    }
    return ret;
  }

  public static getAppInfo(): AppInfo {
    return AppService.appInfo;
  }

  public static getAppVersion(): string {
    return AppService.appInfo.version + ' (' + AppService.appInfo.build + ')';
  }

  public static getAppName(): string {
    return AppService.appInfo.name;
  }

  private static getBrowserWebInfo(): AppInfo {
    let info = {
      name: AppConstants.WEB_APPLICATION_NAME,
      id: AppConstants.WEB_APPLICATION_PACKAGE_ID,
      build: AppConstants.WEB_APPLICATION_BUILD,
      version: AppConstants.WEB_APPLICATION_VERSION
    }
    return info;
  }

  public static isTestingApp(): boolean {
    return AppService.appInfo.id === AppConstants.APP_TESTING_PACKAGE_ID;
  }

}

